export const formatDateHourSeconds = (dated: Date) => {
  if (!dated) return "N/A";
  let hours = new Date(dated).getHours();
  let minutes = new Date(dated).getMinutes();
  let seconds = new Date(dated).getSeconds();
  return `${hours}:${minutes}:${seconds}`;
};

export const enum TimeDiffType {
  days = "days",
  hours = "hours",
  minutes = "minutes",
  seconds = "seconds",
  milliseconds = "milliseconds",
}

export const getDateDiff = (
  startDate: Date,
  endDate: Date,
  type = TimeDiffType.minutes
) => {
  const differenceInMilliseconds =
    new Date(endDate).getTime() - new Date(startDate).getTime();

  let obj = {
    days: Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24)),
    hours: Math.floor(differenceInMilliseconds / (1000 * 60 * 60)),
    minutes: Math.floor(differenceInMilliseconds / (1000 * 60)),
    seconds: Math.floor(differenceInMilliseconds / 1000),
    milliseconds: differenceInMilliseconds,
  };
  return obj[type];
};

export const createDateTime = (startDate: Date, endDate: Date) => {
  const date1 = new Date(startDate);
  const date2 = new Date(endDate);
  // Extract the date from date1
  const year = date1.getFullYear();
  const month = date1.getMonth();
  const day = date1.getDate();

  // Extract the time from date2
  const hours = date2.getHours();
  const minutes = date2.getMinutes();
  const seconds = date2.getSeconds();

  // Create a new Date object with the combined date and time
  const combinedDate = new Date(year, month, day, hours, minutes, seconds);
  return combinedDate;
};

export const addMinutesInDate = (date: Date, minutes: number) => {
  return new Date(date.getTime() + minutes * 60000);
};

export const datesRangeOverlap = (
  range1Start: Date,
  range1End: Date,
  range2Start: Date,
  range2End: Date
) => {
  return !!(range1Start <= range2End && range2Start <= range1End);
};

export const formatDate = (date: Date) => {
  date = new Date(date);
  let day = date.getDate();
  let month = date.getMonth();
  let year = date.getFullYear();
  let format1 = month + 1 + "/" + day + "/" + year;
  let format2 = day + "/" + (month + 1) + "/" + year;
  let format3 = month + 1 + "-" + day + "-" + year;
  let format4 = day + "-" + (month + 1) + "-" + year;
  let formatshortYear = month + 1 + "/" + day + "/" + +(year % 100);

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const formatNumeric: string = date.toLocaleDateString("en-US", options);

  return { format1, format2, format3, format4, formatshortYear, formatNumeric };
};

export const ISOformatedDateTime = (date: Date) => {
  const formatedDate = new Date(date).toLocaleDateString();
  const formatedTime = new Date(date).toLocaleTimeString(navigator.language, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return formatedDate + " " + formatedTime;
};

export function getTimeInAMPMFormat(date: Date) {
  const hours = new Date(date).getHours();
  const minutes = new Date(date).getMinutes();

  const meridiem = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes} ${meridiem}`;
}

export function isCurrentDateInRange(startDate: Date, endDate: Date) {
  const currentDate = new Date();
  return currentDate >= startDate && currentDate <= endDate;
}

export const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function getDateRanges() {
  const today = new Date();

  // Next Week
  const nextWeekStart = new Date(today);
  nextWeekStart.setDate(today.getDate() + 7 - today.getDay());
  nextWeekStart.setHours(0, 0, 0, 0);

  const nextWeekEnd = new Date(today);
  nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
  nextWeekEnd.setHours(23, 59, 59, 999);

  // This Week
  const thisWeekStart = new Date(today);
  thisWeekStart.setDate(today.getDate() - today.getDay());
  thisWeekStart.setHours(0, 0, 0, 0);

  const thisWeekEnd = new Date(today);
  thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
  thisWeekEnd.setHours(23, 59, 59, 999);

  // This Month
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  thisMonthStart.setHours(0, 0, 0, 0);

  const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const thisMonthEnd = new Date(nextMonthStart.getTime() - 1);
  thisMonthEnd.setHours(23, 59, 59, 999);

  return {
    nextWeek: [nextWeekStart, nextWeekEnd],
    thisWeek: [thisWeekStart, thisWeekEnd],
    thisMonth: [thisMonthStart, thisMonthEnd],
  };
}

export const get7Days = () => {
  const currentDate = new Date();

  const weekArray = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(currentDate);
    date.setDate(currentDate.getDate() + i);

    const dayName = date.toLocaleDateString("en-US", { weekday: "short" });

    weekArray.push({ day: dayName, date: date });
  }
  return weekArray;
};

export const formatTime = (date: Date) => {
  const Hr_min = new Date(date).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return { Hr_min };
};

export function isTodayTomorrowsDate(date: Date) {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  date = new Date(date);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return "Tomorrow";
  } else {
    return formatDate(date).format2;
  }
}

export const daysOfWeekShort = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function addDaysToDate(date: Date, daysToAdd: number): Date {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + daysToAdd);
  return newDate;
}

export function isAdult(birthdate: Date): boolean {
  const today = new Date();
  const ageDifferenceInMilliseconds =
    today.getTime() - new Date(birthdate).getTime();
  const ageInYears =
    ageDifferenceInMilliseconds / (1000 * 60 * 60 * 24 * 365.25); // Account for leap years
  return ageInYears >= 18;
}
